/**
 * Promotions — the generosity desk (OC1).
 *
 * This screen SURFACES what P2–P5 already built server-side (ADR-038/040); it
 * invents nothing. Every lever here is a config write through an audited admin
 * route, and no money moves from this screen — a bonus is credited only through
 * the wallet, in the consumer and redeem paths.
 *
 * The levers, in owner language:
 *   · Deposit-match %      — how much of a deposit is matched as bonus (`matchBps`)
 *   · Max bonus            — the ceiling per claim (`maxBonus`)
 *   · Wagering ×           — how many times the bonus must be wagered before it
 *                            unlocks. LOWER is more generous.
 *   · Rakeback %           — the share of rake returned to a player at a VIP tier
 *   · Game weight %        — how much a stake on a game counts toward wagering
 *
 * The outstanding-liability band up top is the budget reality against which all
 * of that is set: what the platform still owes as un-wagered bonus, by currency.
 */
import { useState, type ReactNode } from 'react';
import {
  createCampaign,
  createCode,
  fetchCampaigns,
  fetchCodes,
  fetchPromoLiability,
  fetchPromoWeights,
  fetchVipTiers,
  updateCampaign,
  updateCode,
  updatePromoWeight,
  upsertVipTier,
} from '../api/client.ts';
import type { PromoCampaign, PromoCode, PromoWeight, VipTier } from '../api/types.ts';
import { useAsync } from '../lib/useAsync.ts';
import { isAmount } from '../lib/money.ts';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Money,
  TableSkeleton,
} from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { IconCheck, IconDie, IconGauge, IconRefresh, IconSliders, IconTrend } from '../ui/icons.tsx';

/** bps → a percentage string with two places. 1500 → "15.00". */
const bpsToPct = (bps: number): string => (bps / 100).toFixed(2);
/** A "%" input string back to integer bps. "15" → 1500. null when unparseable. */
function pctToBps(pct: string): number | null {
  const t = pct.trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

export function Promotions(): ReactNode {
  const campaigns = useAsync(() => fetchCampaigns(), []);
  const [selected, setSelected] = useState<number | null>(null);

  const rows = campaigns.data ?? [];
  const active = rows.find((c) => c.id === selected) ?? null;

  return (
    <div className="stack">
      <LiabilityBand />

      <div className="split">
        <div className="stack">
          <div className="toolbar">
            <span className="toolbar__note">
              Generosity levers — deposit match, wagering, budget caps. Nothing here pays out; a
              bonus is credited only through the wallet when a player claims and wagers it.
            </span>
            <button type="button" className="btn btn--sm btn--ghost" onClick={campaigns.reload}>
              <IconRefresh size="sm" />
              Refresh
            </button>
          </div>

          <Card title="Campaigns" icon={<IconTrend />} count={rows.length} flush>
            {campaigns.error && rows.length === 0 ? (
              <ErrorState title="Campaigns did not load" detail={campaigns.error} onRetry={campaigns.reload} />
            ) : campaigns.loading && rows.length === 0 ? (
              <TableSkeleton columns={['text', 'text', 'num', 'num', 'text']} rows={5} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<IconTrend size="lg" />}
                title="No campaigns yet"
                hint="Create one below. It stays disabled until you enable it — nothing pays until then."
              />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Kind</th>
                      <th className="right">Match</th>
                      <th className="right">Wager ×</th>
                      <th>State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((c) => (
                      <tr
                        key={c.id}
                        className={`row--clickable${c.id === selected ? ' is-selected' : ''}`}
                        onClick={() => setSelected(c.id)}
                      >
                        <td className="nowrap cell-lead">{c.name}</td>
                        <td className="muted">{c.kind === 'deposit_match' ? 'deposit match' : 'code'}</td>
                        <td className="right mono">{bpsToPct(c.matchBps)}%</td>
                        <td className="right mono">{c.wagerMultiplier}×</td>
                        <td>
                          {!c.enabled ? (
                            <Badge tone="neutral">disabled</Badge>
                          ) : c.paused ? (
                            <Badge tone="pending">paused</Badge>
                          ) : (
                            <Badge tone="win">live</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <NewCampaignCard onCreated={campaigns.reload} />
        </div>

        <div className="stack">
          <CampaignPanel campaign={active} onChanged={campaigns.reload} />
          <VipTiersCard />
          <WeightsCard />
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ liability

function LiabilityBand(): ReactNode {
  const liability = useAsync(() => fetchPromoLiability(), []);
  const perCurrency = liability.data?.perCurrency ?? [];

  return (
    <Card title="Outstanding bonus liability" icon={<IconGauge />} sub="Un-wagered bonus the platform still owes, by currency">
      {liability.loading && !liability.data ? (
        <TableSkeleton columns={['text', 'num', 'num']} rows={2} />
      ) : perCurrency.length === 0 ? (
        <p className="field__hint field__hint--block">No active bonus liability. Nothing is encumbered.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Currency</th>
                <th className="right">Locked (un-wagered)</th>
                <th className="right">Active grants</th>
              </tr>
            </thead>
            <tbody>
              {perCurrency.map((r) => (
                <tr key={r.currency}>
                  <td className="mono">{r.currency}</td>
                  <td className="right mono"><Money value={r.locked} /></td>
                  <td className="right mono">{r.activeGrants}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ campaign panel

function CampaignPanel({ campaign, onChanged }: { campaign: PromoCampaign | null; onChanged: () => void }): ReactNode {
  const [matchPct, setMatchPct] = useState('');
  const [maxBonus, setMaxBonus] = useState('');
  const [wager, setWager] = useState('');
  const [minDeposit, setMinDeposit] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  if (!campaign) {
    return (
      <Card title="Campaign settings" icon={<IconSliders />}>
        <EmptyState compact icon={<IconSliders size="lg" />} title="No campaign selected" hint="Pick a campaign to tune its generosity." />
      </Card>
    );
  }

  if (editing !== campaign.id) {
    setEditing(campaign.id);
    setMatchPct(bpsToPct(campaign.matchBps));
    setMaxBonus(campaign.maxBonus);
    setWager(String(campaign.wagerMultiplier));
    setMinDeposit(campaign.minDeposit);
  }

  const matchBps = pctToBps(matchPct);
  const wagerNum = /^\d{1,4}$/.test(wager.trim()) ? Number(wager.trim()) : null;
  const valid = matchBps !== null && isAmount(maxBonus) && wagerNum !== null && isAmount(minDeposit);

  async function toggle(field: 'enabled' | 'paused'): Promise<void> {
    if (!campaign) return;
    const next = !campaign[field];
    const result = await confirm({
      title: field === 'enabled' ? (next ? 'Enable campaign' : 'Disable campaign') : next ? 'Pause campaign' : 'Resume campaign',
      intent: (field === 'enabled' && !next) || (field === 'paused' && next) ? 'danger' : 'default',
      body:
        field === 'enabled'
          ? next
            ? 'Players can claim this campaign immediately.'
            : 'Claims stop at once. Grants already made keep wagering.'
          : next
            ? 'New claims stop while paused; existing grants are unaffected.'
            : 'Claims resume.',
      confirmLabel: 'Confirm',
      facts: [{ label: 'Campaign', value: campaign.name }],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      await updateCampaign(campaign.id, { [field]: next });
      toast(`${campaign.name} ${field} → ${next}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the campaign.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function save(): Promise<void> {
    if (!campaign || !valid || matchBps === null || wagerNum === null) return;
    const result = await confirm({
      title: 'Save generosity',
      body: 'Applies to claims made from now. Grants already issued keep the terms they were issued under.',
      confirmLabel: 'Save',
      facts: [
        { label: 'Campaign', value: campaign.name },
        { label: 'Deposit match', value: `${matchPct}%` },
        { label: 'Max bonus', value: maxBonus },
        { label: 'Wagering', value: `${wager}×` },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      await updateCampaign(campaign.id, { matchBps, maxBonus, wagerMultiplier: wagerNum, minDeposit });
      toast(`Generosity saved for ${campaign.name}.`);
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not save.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <Card
        title={campaign.name}
        icon={<IconTrend />}
        actions={
          <>
            {campaign.welcome ? <Badge tone="info">welcome</Badge> : null}
            {!campaign.enabled ? <Badge tone="neutral">disabled</Badge> : campaign.paused ? <Badge tone="pending">paused</Badge> : <Badge tone="win">live</Badge>}
          </>
        }
      >
        <label className="field">
          <span className="field__label">Deposit match, %</span>
          <input className="input num" value={matchPct} placeholder="100.00" onChange={(e) => setMatchPct(e.target.value)} />
          {matchBps === null ? <span className="field__error">A percentage, up to two places (0–10000%).</span> : null}
        </label>

        <label className="field">
          <span className="field__label">Max bonus (per claim)</span>
          <input className="input num" value={maxBonus} onChange={(e) => setMaxBonus(e.target.value)} />
          {!isAmount(maxBonus) ? <span className="field__error">Decimal, up to four places.</span> : null}
        </label>

        <label className="field">
          <span className="field__label">Wagering requirement (× the bonus) — lower is more generous</span>
          <input className="input num" value={wager} placeholder="20" onChange={(e) => setWager(e.target.value)} />
          {wagerNum === null ? <span className="field__error">A whole number.</span> : null}
        </label>

        <label className="field">
          <span className="field__label">Minimum deposit to qualify</span>
          <input className="input num" value={minDeposit} onChange={(e) => setMinDeposit(e.target.value)} />
          {!isAmount(minDeposit) ? <span className="field__error">Decimal, up to four places.</span> : null}
        </label>

        <div className="row-actions row-actions--start">
          <button type="button" className="btn btn--primary btn--sm" disabled={busy || !valid} onClick={() => void save()}>
            <IconCheck size="sm" />
            Save generosity
          </button>
          <button type="button" className={`btn btn--sm ${campaign.enabled ? 'btn--danger' : 'btn--primary'}`} disabled={busy} onClick={() => void toggle('enabled')}>
            {campaign.enabled ? 'Disable' : 'Enable'}
          </button>
          {campaign.enabled ? (
            <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => void toggle('paused')}>
              {campaign.paused ? 'Resume' : 'Pause'}
            </button>
          ) : null}
        </div>
      </Card>

      {campaign.kind === 'code' ? <CodesCard campaign={campaign} /> : null}
    </div>
  );
}

// ------------------------------------------------------------------ codes

function CodesCard({ campaign }: { campaign: PromoCampaign }): ReactNode {
  const codes = useAsync(() => fetchCodes(campaign.id), [campaign.id]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const rows = codes.data ?? [];

  async function add(): Promise<void> {
    const code = draft.trim();
    if (!/^[A-Za-z0-9_-]{2,64}$/.test(code)) {
      toast('A code is 2–64 letters, digits, - or _.', 'error');
      return;
    }
    setBusy(true);
    try {
      await createCode({ campaignId: campaign.id, code });
      toast(`Code ${code.toUpperCase()} created.`);
      setDraft('');
      codes.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not create the code.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(row: PromoCode): Promise<void> {
    setBusy(true);
    try {
      await updateCode(row.id, { enabled: !row.enabled });
      codes.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not update the code.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Redeem codes" icon={<IconSliders />} count={rows.length}>
      <label className="field">
        <span className="field__label">New code</span>
        <div className="row-actions row-actions--start">
          <input className="input mono" value={draft} placeholder="WELCOME100" onChange={(e) => setDraft(e.target.value)} />
          <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={() => void add()}>Add</button>
        </div>
      </label>
      {rows.length === 0 ? (
        <p className="field__hint field__hint--block">No codes yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Code</th><th className="right">Max claims</th><th>State</th><th></th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="mono nowrap">{row.code}</td>
                  <td className="right mono">{row.maxClaims ?? '∞'}</td>
                  <td><Badge tone={row.enabled ? 'win' : 'neutral'}>{row.enabled ? 'enabled' : 'disabled'}</Badge></td>
                  <td className="right">
                    <button type="button" className="btn btn--sm btn--ghost" disabled={busy} onClick={() => void toggle(row)}>
                      {row.enabled ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ new campaign

function NewCampaignCard({ onCreated }: { onCreated: () => void }): ReactNode {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<'deposit_match' | 'code'>('deposit_match');
  const [matchPct, setMatchPct] = useState('100');
  const [maxBonus, setMaxBonus] = useState('5000.0000');
  const [wager, setWager] = useState('20');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const matchBps = pctToBps(matchPct);
  const wagerNum = /^\d{1,4}$/.test(wager.trim()) ? Number(wager.trim()) : null;
  const valid = name.trim().length >= 3 && matchBps !== null && isAmount(maxBonus) && wagerNum !== null;

  async function create(): Promise<void> {
    if (!valid || matchBps === null || wagerNum === null) return;
    setBusy(true);
    try {
      await createCampaign({ name: name.trim(), kind, matchBps, maxBonus, wagerMultiplier: wagerNum });
      toast(`Campaign "${name.trim()}" created (disabled).`);
      setName('');
      onCreated();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not create the campaign.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="New campaign" icon={<IconTrend />} sub="Created disabled — nothing pays until you enable it">
      <label className="field">
        <span className="field__label">Name</span>
        <input className="input" value={name} placeholder="Diwali deposit match" onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">Kind</span>
        <div className="row-actions row-actions--start">
          <button type="button" className={`btn btn--sm ${kind === 'deposit_match' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setKind('deposit_match')}>Deposit match</button>
          <button type="button" className={`btn btn--sm ${kind === 'code' ? 'btn--primary' : 'btn--ghost'}`} onClick={() => setKind('code')}>Code</button>
        </div>
      </label>
      <label className="field">
        <span className="field__label">Deposit match, %</span>
        <input className="input num" value={matchPct} onChange={(e) => setMatchPct(e.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">Max bonus</span>
        <input className="input num" value={maxBonus} onChange={(e) => setMaxBonus(e.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">Wagering ×</span>
        <input className="input num" value={wager} onChange={(e) => setWager(e.target.value)} />
      </label>
      <div className="row-actions row-actions--start">
        <button type="button" className="btn btn--primary btn--sm" disabled={busy || !valid} onClick={() => void create()}>
          <IconCheck size="sm" />
          Create campaign
        </button>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ VIP tiers

function VipTiersCard(): ReactNode {
  const tiers = useAsync(() => fetchVipTiers(), []);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const toast = useToast();
  const rows = tiers.data ?? [];

  async function saveRakeback(tier: VipTier): Promise<void> {
    const draft = drafts[tier.tier] ?? bpsToPct(tier.rakebackBps);
    const bps = pctToBps(draft);
    if (bps === null || bps > 10000) {
      toast('Rakeback is 0–100%, up to two places.', 'error');
      return;
    }
    setBusy(true);
    try {
      await upsertVipTier({ tier: tier.tier, rakebackBps: bps });
      toast(`${tier.name} rakeback → ${draft}%.`);
      tiers.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not save the tier.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="VIP rakeback" icon={<IconGauge />} sub="The share of rake returned to a player at each loyalty tier" count={rows.length}>
      {tiers.loading && rows.length === 0 ? (
        <TableSkeleton columns={['text', 'num', 'num']} rows={4} />
      ) : rows.length === 0 ? (
        <p className="field__hint field__hint--block">No VIP tiers configured.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Tier</th><th className="right">XP threshold</th><th className="right">Rakeback %</th><th></th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.tier}>
                  <td className="nowrap cell-lead">{row.tier} · {row.name}</td>
                  <td className="right mono dim">{row.xpThreshold}</td>
                  <td className="right">
                    <input
                      className="input num"
                      style={{ width: '6rem' }}
                      value={drafts[row.tier] ?? bpsToPct(row.rakebackBps)}
                      onChange={(e) => setDrafts((d) => ({ ...d, [row.tier]: e.target.value }))}
                    />
                  </td>
                  <td className="right">
                    <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={() => void saveRakeback(row)}>Save</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ game weights

function WeightsCard(): ReactNode {
  const weights = useAsync(() => fetchPromoWeights(), []);
  const [busy, setBusy] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const toast = useToast();
  const rows = weights.data ?? [];

  async function save(row: PromoWeight): Promise<void> {
    const draft = drafts[row.gameSlug] ?? bpsToPct(row.weightBps);
    const bps = pctToBps(draft);
    if (bps === null || bps > 10000) {
      toast('A weight is 0–100%, up to two places.', 'error');
      return;
    }
    setBusy(true);
    try {
      await updatePromoWeight(row.gameSlug, bps);
      toast(`${row.gameSlug} weight → ${draft}%.`);
      weights.reload();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not save the weight.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Wagering game weights" icon={<IconDie />} sub="How much a stake on each game counts toward a bonus's wagering — never a payout" count={rows.length}>
      {weights.loading && rows.length === 0 ? (
        <TableSkeleton columns={['text', 'num']} rows={4} />
      ) : rows.length === 0 ? (
        <p className="field__hint field__hint--block">No game weights configured.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Game</th><th className="right">Counts %</th><th></th></tr></thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.gameSlug}>
                  <td className="mono nowrap">{row.gameSlug}</td>
                  <td className="right">
                    <input
                      className="input num"
                      style={{ width: '6rem' }}
                      value={drafts[row.gameSlug] ?? bpsToPct(row.weightBps)}
                      onChange={(e) => setDrafts((d) => ({ ...d, [row.gameSlug]: e.target.value }))}
                    />
                  </td>
                  <td className="right">
                    <button type="button" className="btn btn--sm btn--primary" disabled={busy} onClick={() => void save(row)}>Save</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
